/** @format */

/**
 * Formats a share (0 to 1) as a whole percentage, keeping a decimal place for
 * the shares that would otherwise round away to 0%.
 */
export function formatShareAsPercentage(locale: string, share: number): string {
    const normalizedLocale = locale.replace('_', '-');
    const hasDecimalPlace = share > 0 && share < 0.01;

    return share.toLocaleString(normalizedLocale, {
        style: 'percent',
        maximumFractionDigits: hasDecimalPlace ? 1 : 0,
    });
}

/**
 * Whether the reader has asked their browser for less motion.
 *
 * Read at the moment it matters rather than watched, because it is only ever
 * consulted in response to a press. Answers false where there is no browser to
 * ask, which is the safe way round: a server render decides no animation, and the
 * client decides again as soon as anyone interacts.
 */
export function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * How far short of the viewport edge a scrolled-to element comes to rest, so it
 * does not arrive flush against it and read as cut off.
 */
const SCROLL_CLEARANCE = 24;

/**
 * How long the page takes to travel, however far it is going. The browser's own
 * smooth scrolling is faster than this section wants and its speed is not ours to
 * set, which is the whole reason this is done by hand.
 */
const SCROLL_DURATION_MILLISECONDS = 700;

/**
 * How far the page has to move to bring a rectangle into view, and no further.
 *
 * This is what `scrollIntoView`'s `block: 'nearest'` works out: nothing at all if
 * the rectangle already fits on screen, otherwise just enough to sit inside the
 * nearest edge. Taking the smaller of the two candidates is what keeps an element
 * taller than the viewport from being dragged past its own top.
 */
export function getScrollDistance(
    rectangle: Pick<DOMRect, 'top' | 'bottom'>,
    viewportHeight: number
): number {
    const above = rectangle.top - SCROLL_CLEARANCE;
    const below = rectangle.bottom + SCROLL_CLEARANCE - viewportHeight;

    if (above < 0) {
        return above;
    }

    if (below > 0) {
        return Math.min(below, above);
    }

    return 0;
}

/**
 * Slow in, slow out, so the page eases away and settles rather than starting and
 * stopping at full speed.
 */
function ease(progress: number): number {
    return progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - (-2 * progress + 2) ** 3 / 2;
}

let scrollFrame: number | null = null;

/**
 * Scrolls the page the least it can to bring an element into view, over a duration
 * this code controls rather than the browser's.
 *
 * A scroll already under way is abandoned rather than raced, so picking a second
 * card mid-journey retargets instead of fighting itself. Readers who have asked for
 * reduced motion are simply put there.
 */
export function scrollGentlyIntoView(element: HTMLElement | null): void {
    if (!element || typeof window === 'undefined') {
        return;
    }

    const distance = getScrollDistance(
        element.getBoundingClientRect(),
        window.innerHeight
    );

    if (!distance) {
        return;
    }

    if (scrollFrame !== null) {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = null;
    }

    const start = window.scrollY;

    if (prefersReducedMotion()) {
        window.scrollTo(window.scrollX, start + distance);
        return;
    }

    const startedAt = performance.now();

    const step = (now: number) => {
        const progress = Math.min(
            (now - startedAt) / SCROLL_DURATION_MILLISECONDS,
            1
        );

        window.scrollTo(window.scrollX, start + distance * ease(progress));

        scrollFrame = progress < 1 ? window.requestAnimationFrame(step) : null;
    };

    scrollFrame = window.requestAnimationFrame(step);
}
